import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { V, serif, mono, eyebrow, card, btnGold, btnGhost, xpToLevel } from '../theme'

const DASHBOARD_QUOTES = [
  { q: "The most common form of despair is not being who you are.", a: "Søren Kierkegaard" },
  { q: "Knowing yourself is the beginning of all wisdom.", a: "Aristotle" },
  { q: "Until you make the unconscious conscious, it will direct your life and you will call it fate.", a: "Carl Jung" },
  { q: "Risk comes from not knowing what you are doing.", a: "Warren Buffett" },
  { q: "The market is a device for transferring money from the impatient to the patient.", a: "Warren Buffett" },
  { q: "In investing, what is comfortable is rarely profitable.", a: "Robert Arnott" },
  { q: "The goal of a successful trader is to make the best trades. Money is secondary.", a: "Alexander Elder" },
  { q: "Trade what you see, not what you think.", a: "Unknown" },
  { q: "The key to trading success is emotional discipline.", a: "Victor Sperandeo" },
  { q: "It does not matter how slowly you go as long as you do not stop.", a: "Confucius" },
  { q: "Opportunities come infrequently. When it rains gold, put out the bucket, not the thimble.", a: "Warren Buffett" },
  { q: "The first rule is not to lose. The second rule is not to forget the first rule.", a: "Warren Buffett" },
  { q: "An investment in knowledge pays the best interest.", a: "Benjamin Franklin" },
  { q: "Do not save what is left after spending, but spend what is left after saving.", a: "Warren Buffett" },
  { q: "The secret of getting ahead is getting started.", a: "Mark Twain" },
  { q: "Capital preserved is opportunity saved. You cannot trade tomorrow with money you lost today.", a: "Unknown" },
  { q: "She believed she could, so she did.", a: "R.S. Grey" },
  { q: "The most courageous act is still to think for yourself. Aloud.", a: "Coco Chanel" },
  { q: "I am not afraid of storms, for I am learning how to sail my ship.", a: "Louisa May Alcott" },
  { q: "You are not a drop in the ocean. You are the entire ocean in a drop.", a: "Rumi" },
];

const DAILY_RULES = [
  { rule: "Trade the trend, not your opinion.", context: "The chart is telling you something. Your job is to read it, not argue with it." },
  { rule: "The trend is your friend — until it ends.", context: "Ride trends with patience. Exit when the evidence changes, not your feeling." },
  { rule: "Never risk more than 1-2% on a single trade.", context: "Position sizing is the skill that keeps you in the game long enough for the edge to work." },
  { rule: "Close at 50% profit. Don't get greedy.", context: "Taking half the maximum profit on twice as many trades is how consistent wealth is built." },
  { rule: "The first loss is the best loss.", context: "Cutting early is almost always cheaper than holding and hoping." },
  { rule: "Cash is a position.", context: "Not trading when conditions aren't right is a decision. Often the best one." },
  { rule: "Know your exit before you enter.", context: "Define your wrong, your right, and your risk before the market has a chance to pressure you." },
  { rule: "Never trade angry, rushed, or distracted.", context: "The market doesn't know how you feel. But your decisions will reflect it." },
  { rule: "Be fearful when others are greedy, and greedy when others are fearful.", context: "Fear spikes = rich premium. Complacency = thin premium. The gauge is your edge." },
  { rule: "Don't catch a falling knife.", context: "Wait for confirmation before buying a downtrending stock. Let the knife hit the floor first." },
  { rule: "Don't fight the Fed.", context: "Know the direction of monetary policy. Trading against it is swimming upstream." },
  { rule: "Hope is not a trading strategy.", context: "Plans have rules. Wishes don't. Know the difference before you enter." },
  { rule: "Buy the rumor, sell the news.", context: "Markets price in expectations. By the time the news is confirmed, the move is often over." },
  { rule: "Volume precedes price.", context: "Unusual volume before a big move is often smart money positioning. Pay attention." },
  { rule: "If you wouldn't enter this trade fresh today, close it.", context: "The reason you're still in a trade should be as strong today as the day you entered." },
];

function RiskTooltip({ label, explanation, color }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onClick={() => setShow(s => !s)}>
      <div style={{ cursor: 'help', borderBottom: '1px dashed rgba(240,237,230,0.35)', fontSize: '10px', color: 'rgba(240,237,230,0.65)', fontFamily: "'Jost',sans-serif", marginBottom: '4px', userSelect: 'none' }}>{label}</div>
      {show && (
        <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '8px', background: '#1a1a1e', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '8px', padding: '10px 14px', width: '200px', zIndex: 1000, boxShadow: '0 4px 20px rgba(0,0,0,0.6)', pointerEvents: 'none' }}>
          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', fontWeight: 700, color: color || '#c9a84c', marginBottom: '4px' }}>{label}</div>
          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: 'rgba(240,237,230,0.55)', lineHeight: '1.6' }}>{explanation}</div>
        </div>
      )}
    </div>
  );
}


function ProgressDashboard({ state, onNavigate, onUpdate, tradingRulesData }) {
  const now = new Date();
  const hour = now.getHours();
  const greetName = state.studentName && state.studentName !== 'Trader' ? `, ${state.studentName}` : '';
  const greeting = (hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening') + greetName + '.';
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const dayOfYear = Math.floor((now - new Date(now.getFullYear(), 0, 0)) / 86400000);
  const todayQuote = DASHBOARD_QUOTES[dayOfYear % DASHBOARD_QUOTES.length];
  const allBuiltInRules = tradingRulesData.flatMap(cat => cat.rules);
  const favIds = state.favoriteRules || [];
  const favRules = allBuiltInRules.filter(r => favIds.includes(r.id));
  const allCustomRules = state.customRules || [];
  const rulePool = [...favRules, ...allCustomRules];
  const todayRule = rulePool.length > 0
    ? { rule: rulePool[dayOfYear % rulePool.length].text, context: rulePool[dayOfYear % rulePool.length].explanation.substring(0, 130) + '…' }
    : DAILY_RULES[(dayOfYear + 1) % DAILY_RULES.length];

  const { xpTotal = 0, completedChapters = [], plTrades = [], innerWorkCheckins = [],
    whys = [], financialGoals = [], accountSize = 0, accountType = 'cash' } = state;

  const { level } = xpToLevel(xpTotal);

  // Trading stats
  const trades = plTrades || [];
  const closedTrades = trades.filter(t => t.status !== 'Open');
  const openTrades = trades.filter(t => t.status === 'Open');
  const winners = closedTrades.filter(t => parseFloat(t.pnl) > 0);
  const winRate = closedTrades.length >= 2 ? Math.round((winners.length / closedTrades.length) * 100) : null;
  const lastTradeDate = trades.length > 0 ? (trades[trades.length - 1].date || '—') : null;

  // Capital at risk — three-tier calculation
  // Tier 1: currentMark × qty × 100 × 2 (most accurate)
  // Tier 2: premium × qty × 100 × 2 (practical 2x rule — main number shown)
  // Tier 3: spreadWidth × qty × 100 (theoretical max — shown separately, not in main total)
  const calcDashPracticalLoss = (t) => {
    if (['CC', 'Covered Call'].includes(t.strategy)) return 0;
    const qty = parseInt(t.qty) || 1;
    if (t.currentMark != null && parseFloat(t.currentMark) >= 0) {
      return parseFloat(t.currentMark) * qty * 100 * 2;
    }
    return (parseFloat(t.credit) || 0) * qty * 100 * 2;
  };
  const calcDashTheoreticalLoss = (t) => {
    if (['CC', 'Covered Call'].includes(t.strategy)) return 0;
    const qty = parseInt(t.qty) || 1;
    const isSpread = ['0DTE Spread', 'Bull Put Spread', 'Bear Call Spread'].includes(t.strategy);
    if (isSpread) {
      const s1 = parseFloat(t.strike) || 0;
      const s2 = parseFloat(t.strike2) || 0;
      if (s1 > 0 && s2 > 0) return Math.abs(s1 - s2) * qty * 100;
    }
    return calcDashPracticalLoss(t);
  };
  const anyOpenWithoutMark = openTrades.some(t => t.currentMark == null);
  const capitalAtRisk = openTrades.reduce((sum, t) => sum + calcDashPracticalLoss(t), 0);
  const theoreticalMaxLoss = openTrades.reduce((sum, t) => sum + calcDashTheoreticalLoss(t), 0);
  const riskPct = accountSize > 0 ? (capitalAtRisk / accountSize) * 100 : null;
  const riskColor = capitalAtRisk === 0 ? V.sage : riskPct === null ? V.gold : riskPct < 2 ? V.sage : riskPct < 5 ? V.gold : V.rose;

  // Inner work stats
  const checkins = innerWorkCheckins || [];

  // Whys
  const _savedWhys = (whys && whys.length > 0) ? whys : (() => { try { const s = localStorage.getItem('tlaw_whys'); return s ? JSON.parse(s) : []; } catch { return []; } })();
  const whysSet = _savedWhys.length > 0;
  const [editingWhys, setEditingWhys] = useState(false);
  const [whyInputs, setWhyInputs] = useState(() => _savedWhys.length > 0 ? [..._savedWhys, '', ''].slice(0, 3) : ['', '', '']);
  const handleWhyChange = (i, val) => {
    const n = [...whyInputs]; n[i] = val; setWhyInputs(n);
    try { localStorage.setItem('tlaw_whys', JSON.stringify(n.map(w => w.trim()).filter(Boolean))); } catch {}
  };
  const saveWhys = () => {
    const cleaned = whyInputs.map(w => w.trim()).filter(Boolean);
    onUpdate && onUpdate({ whys: cleaned });
    try { localStorage.setItem('tlaw_whys', JSON.stringify(cleaned)); } catch {}
    setEditingWhys(false);
  };

  // Goals
  const goals = (financialGoals && financialGoals.length > 0) ? financialGoals : (() => { try { const s = localStorage.getItem('tlaw_goals'); return s ? JSON.parse(s) : []; } catch { return []; } })();
  const [addingGoal, setAddingGoal] = useState(false);
  const [goalForm, setGoalForm] = useState({ name: '', startingAmount: '', targetAmount: '', monthlyContribution: '', targetDate: '', calcMode: 'contribution', showOnDashboard: true });
  const [editGoalIdx, setEditGoalIdx] = useState(null);
  const [editGoalForm, setEditGoalForm] = useState({});
  const [updateProgressIdx, setUpdateProgressIdx] = useState(null);
  const [updateProgressAmount, setUpdateProgressAmount] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Risk card inline edit
  const lsGetRsc = (k, def) => { try { const v = localStorage.getItem(k); return v !== null ? JSON.parse(v) : def; } catch { return def; } };
  const [showRiskEdit,  setShowRiskEdit]  = useState(false);
  const [rscSaved,      setRscSaved]      = useState(false);
  const [editAcctType,  setEditAcctType]  = useState('cash');
  const [editBaseline,  setEditBaseline]  = useState('');
  const [editPerTrade,  setEditPerTrade]  = useState('2');
  const [editDailyMax,  setEditDailyMax]  = useState('5');
  const [editWeeklyMax, setEditWeeklyMax] = useState('10');
  const [editFloor,     setEditFloor]     = useState('');
  const openRscEdit = () => {
    setEditAcctType(lsGetRsc('tlaw_risk_accountType', 'cash'));
    setEditBaseline(String(lsGetRsc('tlaw_risk_baseline', '') || ''));
    setEditPerTrade(String(lsGetRsc('tlaw_risk_perTrade',  2)));
    setEditDailyMax(String(lsGetRsc('tlaw_risk_dailyMax',  5)));
    setEditWeeklyMax(String(lsGetRsc('tlaw_risk_weeklyMax', 10)));
    setEditFloor(String(lsGetRsc('tlaw_risk_floor', '') || ''));
    setRscSaved(false);
    setShowRiskEdit(true);
  };
  const saveRscEdit = () => {
    const bl = parseFloat(editBaseline) || 0;
    const dm = parseFloat(editDailyMax)  || 5;
    const wm = parseFloat(editWeeklyMax) || 10;
    const pt = parseFloat(editPerTrade)  || 2;
    const fl = parseFloat(editFloor)     || Math.round(bl * 0.8);
    try {
      localStorage.setItem('tlaw_risk_accountType', JSON.stringify(editAcctType));
      localStorage.setItem('tlaw_risk_baseline',    JSON.stringify(bl));
      localStorage.setItem('tlaw_risk_dailyMax',    JSON.stringify(dm));
      localStorage.setItem('tlaw_risk_weeklyMax',   JSON.stringify(wm));
      localStorage.setItem('tlaw_risk_perTrade',    JSON.stringify(pt));
      localStorage.setItem('tlaw_risk_floor',       JSON.stringify(fl));
    } catch {}
    setRscSaved(true);
    setShowRiskEdit(false);
    setTimeout(() => setRscSaved(false), 2000);
  };

  const computeProjected = (start, target, monthly) => {
    const s = parseFloat(start) || 0, t = parseFloat(target) || 0, m = parseFloat(monthly) || 0;
    if (m <= 0 || t <= s) return null;
    const months = Math.ceil((t - s) / m);
    return new Date(now.getFullYear(), now.getMonth() + months, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };
  const computeMonthlyNeeded = (start, target, dateStr) => {
    if (!dateStr) return null;
    const s = parseFloat(start) || 0, t = parseFloat(target) || 0;
    const targetD = new Date(dateStr + 'T00:00:00');
    const months = Math.max(1, Math.round((targetD - now) / (1000 * 60 * 60 * 24 * 30.44)));
    if (t <= s || months <= 0) return null;
    return Math.ceil((t - s) / months);
  };
  const saveNewGoal = () => {
    if (!goalForm.name) return;
    const projected = goalForm.calcMode === 'contribution' ? computeProjected(goalForm.startingAmount, goalForm.targetAmount, goalForm.monthlyContribution) : null;
    const startAmt = parseFloat(goalForm.startingAmount) || 0;
    const targetAmt = parseFloat(goalForm.targetAmount) || 0;
    const monthlyContrib = parseFloat(goalForm.monthlyContribution) || 0;
    const isTargetDate = goalForm.calcMode === 'date';
    const monthlyNeeded = isTargetDate ? (computeMonthlyNeeded(startAmt, targetAmt, goalForm.targetDate) || 0) : 0;
    const newGoals = [...goals, {
      id: Date.now(), name: goalForm.name,
      startingAmount: startAmt,
      currentAmount: startAmt,
      targetAmount: targetAmt,
      calcMode: goalForm.calcMode,
      monthlyContribution: monthlyContrib,
      targetDate: goalForm.targetDate, projectedDate: projected,
      showOnDashboard: goalForm.showOnDashboard, createdAt: new Date().toISOString(),
      contributionType: isTargetDate ? 'targetDate' : 'monthly',
      monthlyAmount: isTargetDate ? monthlyNeeded : monthlyContrib,
    }];
    onUpdate && onUpdate({ financialGoals: newGoals });
    try { localStorage.setItem('tlaw_goals', JSON.stringify(newGoals)); } catch {}
    setGoalForm({ name: '', startingAmount: '', targetAmount: '', monthlyContribution: '', targetDate: '', calcMode: 'contribution', showOnDashboard: true });
    setAddingGoal(false);
  };
  const saveEditGoal = () => {
    const newGoals = goals.map((g, i) => {
      if (i !== editGoalIdx) return g;
      const newTarget = parseFloat(editGoalForm.targetAmount) || 0;
      const newDate = editGoalForm.targetDate || g.targetDate || '';
      let newMonthlyAmount = g.monthlyAmount;
      if ((g.contributionType === 'targetDate' || g.calcMode === 'date') && newDate) {
        newMonthlyAmount = computeMonthlyNeeded(g.currentAmount || 0, newTarget, newDate) || 0;
      }
      return { ...g, ...editGoalForm, targetAmount: newTarget, monthlyAmount: newMonthlyAmount };
    });
    onUpdate && onUpdate({ financialGoals: newGoals });
    try { localStorage.setItem('tlaw_goals', JSON.stringify(newGoals)); } catch {}
    setEditGoalIdx(null); setEditGoalForm({});
  };
  const saveProgress = (idx) => {
    const added = parseFloat(updateProgressAmount) || 0;
    const newGoals = goals.map((g, i) => {
      if (i !== idx) return g;
      const newCurrentAmount = (g.currentAmount || 0) + added;
      let newMonthlyAmount = g.monthlyAmount;
      if ((g.contributionType === 'targetDate' || g.calcMode === 'date') && g.targetDate && g.targetAmount) {
        newMonthlyAmount = computeMonthlyNeeded(newCurrentAmount, g.targetAmount, g.targetDate) || 0;
      }
      return { ...g, currentAmount: newCurrentAmount, monthlyAmount: newMonthlyAmount };
    });
    onUpdate && onUpdate({ financialGoals: newGoals });
    try { localStorage.setItem('tlaw_goals', JSON.stringify(newGoals)); } catch {}
    setUpdateProgressIdx(null); setUpdateProgressAmount('');
  };
  const deleteGoal = (goalId) => {
    const newGoals = goals.filter(g => (g.id || null) !== goalId);
    onUpdate && onUpdate({ financialGoals: newGoals });
    try { localStorage.setItem('tlaw_goals', JSON.stringify(newGoals)); } catch {}
    setDeleteConfirmId(null);
    setEditGoalIdx(null);
    setEditGoalForm({});
  };

  // Responsive layout
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [showRiskDetails, setShowRiskDetails] = useState(false);
  const [weekTab, setWeekTab] = useState('events');
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // ── Market Brief live data ───────────────────────────────────────
  const MB_CACHE_MS = 15 * 60 * 1000; // 15 minutes
  const [mbData, setMbData] = useState(null);
  const [mbLoading, setMbLoading] = useState(false);
  const [mbLastFetch, setMbLastFetch] = useState(null);

  const fetchMB = async (force = false) => {
    const cacheKey = 'tlaw_marketbrief';
    if (!force) {
      try {
        const cached = JSON.parse(localStorage.getItem(cacheKey));
        if (cached && cached.data && cached.timestamp && (Date.now() - cached.timestamp) < MB_CACHE_MS) {
          setMbData(cached.data);
          setMbLastFetch(cached.timestamp);
          return;
        }
      } catch {}
    }
    setMbLoading(true);
    try {
      const res = await fetch('/api/marketbrief');
      if (res.ok) {
        const data = await res.json();
        const ts = Date.now();
        try { localStorage.setItem(cacheKey, JSON.stringify({ date: new Date().toDateString(), timestamp: ts, data })); } catch {}
        setMbData(data);
        setMbLastFetch(ts);
      }
    } catch {}
    setMbLoading(false);
  };

  useEffect(() => { fetchMB(); }, []); // eslint-disable-line

  const mbMinutesAgo = mbLastFetch ? Math.floor((Date.now() - mbLastFetch) / 60000) : null;
  const cardBase = { background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '10px', padding: '22px 24px' };
  const inp = { background: V.bg3, border: `0.5px solid ${V.border}`, borderRadius: '8px', padding: '10px 14px', color: V.text, fontSize: '13px', fontFamily: "'Jost', sans-serif", outline: 'none', width: '100%', boxSizing: 'border-box' };

  // ── Card JSX variables ──────────────────────────────────────────

  const greetingCard = (
    <div style={{ marginBottom: '20px' }}>
      <h1 style={{ ...serif, fontSize: '38px', fontWeight: 400, color: V.gold, margin: '0 0 6px', lineHeight: 1.1 }}>{greeting}</h1>
      <div style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost', sans-serif", marginBottom: '8px' }}>{dateStr}</div>
      <p style={{ ...serif, fontSize: '14px', color: V.muted, fontStyle: 'italic', margin: 0 }}>The market will be here tomorrow. So will you.</p>
    </div>
  );

  const quoteCard = (
    <div style={{ ...cardBase, borderLeft: `3px solid ${V.gold}`, marginBottom: '16px', padding: isMobile ? '10px 14px' : '12px 16px' }}>
      <div style={{ fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase', color: V.muted, fontFamily: "'Jost', sans-serif", marginBottom: '6px' }}>Today's Reflection</div>
      <p style={{ ...serif, fontSize: isMobile ? '13px' : '16px', fontStyle: 'italic', color: V.gold, lineHeight: 1.4, margin: '0 0 5px' }}>"{todayQuote.q}"</p>
      <div style={{ fontSize: '11px', color: V.muted, fontFamily: "'Jost', sans-serif" }}>— {todayQuote.a}</div>
    </div>
  );

  const ruleCard = (
    <div style={{ ...cardBase, borderLeft: `2px solid rgba(201,168,76,0.4)`, marginBottom: '16px', padding: isMobile ? '10px 14px' : '12px 16px' }}>
      <div style={{ fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase', color: V.muted, fontFamily: "'Jost', sans-serif", marginBottom: '6px' }}>Today's Trading Rule</div>
      <p style={{ fontSize: isMobile ? '12px' : '13px', color: V.gold, fontFamily: "'Jost', sans-serif", fontWeight: 500, margin: '0 0 4px', lineHeight: 1.4 }}>{todayRule.rule}</p>
      <p style={{ fontSize: '11px', color: V.muted, fontFamily: "'Jost', sans-serif", fontStyle: 'italic', margin: 0, lineHeight: 1.5 }}>{todayRule.context}</p>
    </div>
  );

  const riskSummaryCard = (() => {
    const rsGet = (k) => { try { const v = localStorage.getItem(k); return v !== null ? JSON.parse(v) : null; } catch { return null; } };
    const fmtAbbrev = (n) => {
      if (!n || n <= 0) return '—';
      if (n >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M';
      if (n >= 10000)   return '$' + Math.round(n / 1000) + 'k';
      if (n >= 1000)    return '$' + (n / 1000).toFixed(1) + 'k';
      return '$' + Math.round(n).toLocaleString();
    };
    const rsBaseline  = parseFloat(rsGet('tlaw_risk_baseline'))  || 0;
    const rsDailyMax  = parseFloat(rsGet('tlaw_risk_dailyMax'))  || 5;
    const rsPerTrade  = parseFloat(rsGet('tlaw_risk_perTrade'))  || 2;
    const rsWeeklyMax = parseFloat(rsGet('tlaw_risk_weeklyMax')) || 10;
    const rsFloor     = parseFloat(rsGet('tlaw_risk_floor'))     || 0;
    const rsAcctType  = rsGet('tlaw_risk_accountType') || 'cash';
    const acctLabel   = rsAcctType === 'margin' ? 'Margin' : rsAcctType === 'portfolio' ? 'Portfolio margin' : 'Cash';
    const dailyStop     = rsBaseline * rsDailyMax  / 100;
    const perTradeLimit = rsBaseline * rsPerTrade  / 100;
    const weeklyStop    = rsBaseline * rsWeeklyMax / 100;
    const buffer        = rsFloor > 0 ? rsBaseline - rsFloor : 0;
    const bufferRatio   = rsBaseline > 0 ? buffer / rsBaseline : 0;
    const bufferColor   = bufferRatio >= 0.2 ? V.sage : bufferRatio >= 0.1 ? V.gold : '#e53935';
    // Current exposure — CC excluded; 2x premium for CSP/Wheel/LEAPS; spread width minus premium for spreads
    const calcTradeRisk = (t) => {
      if (['CC', 'Covered Call'].includes(t.strategy)) return 0;
      const qty    = parseInt(t.qty) || 1;
      const credit = parseFloat(t.credit) || 0;
      const isSpread = ['Bull Put Spread', 'Bear Call Spread', '0DTE Spread'].includes(t.strategy);
      if (isSpread) {
        const s1 = parseFloat(t.strike) || 0;
        const s2 = parseFloat(t.strike2) || 0;
        const width = Math.abs(s1 - s2);
        return Math.max(0, (width - credit) * 100 * qty);
      }
      return credit * 2 * 100 * qty;
    };
    const rsOpenTrades   = openTrades || [];
    const totalAtRisk    = rsOpenTrades.reduce((sum, t) => sum + calcTradeRisk(t), 0);
    const riskPct        = rsBaseline > 0 ? totalAtRisk / rsBaseline * 100 : 0;
    const expColor       = riskPct < 2 ? V.sage : riskPct < 5 ? V.gold : '#e53935';
    const meterFill      = dailyStop > 0 ? Math.min(100, totalAtRisk / dailyStop * 100) : 0;
    const _td = new Date();
    const todayKey = _td.getFullYear() + '-' + String(_td.getMonth() + 1).padStart(2, '0') + '-' + String(_td.getDate()).padStart(2, '0');
    const rsDailyLogs = (() => { try { const v = localStorage.getItem('tlaw_risk_dailyLogs'); return v ? JSON.parse(v) : {}; } catch { return {}; } })();
    const todayLogEntry = rsDailyLogs[todayKey] || {};
    const hasTodayLog = todayLogEntry.pnl !== '' && todayLogEntry.pnl !== undefined && todayLogEntry.pnl !== null;
    const todayPnl = parseFloat(todayLogEntry.pnl) || 0;
    const cardStyle = { background: V.bg2, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '12px', padding: '20px', marginBottom: '20px' };
    const sectionLbl = { fontSize: '9px', letterSpacing: '0.2em', textTransform: 'uppercase', color: V.muted, fontFamily: "'Jost',sans-serif", marginBottom: '8px' };
    const colGrid = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginTop: '8px' };
    const colCell = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' };
    if (!rsBaseline) {
      return (
        <div style={cardStyle}>
          <div style={{ fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase', color: V.gold, fontFamily: "'Jost',sans-serif", marginBottom: '10px' }}>YOUR CAPITAL &amp; RISK LIMITS</div>
          <p style={{ fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', lineHeight: 1.7, margin: '0 0 10px' }}>Set up your Risk Dashboard to see your capital and daily trading limits here every morning.</p>
          <button onClick={() => onNavigate && onNavigate('risk_dashboard')} style={{ padding: '7px 14px', background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: '8px', color: V.goldL, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: '12px', letterSpacing: '0.06em' }}>Set Up Now →</button>
        </div>
      );
    }
    return (
      <div style={cardStyle}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <div style={{ fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase', color: V.gold, fontFamily: "'Jost',sans-serif" }}>YOUR CAPITAL &amp; RISK LIMITS</div>
          <button onClick={() => onNavigate && onNavigate('risk_dashboard')} style={{ background: 'none', border: `0.5px solid ${V.border}`, borderRadius: '6px', padding: '4px 10px', color: V.muted, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: '11px' }}>Full Dashboard →</button>
        </div>
        {/* Section 1 — Capital */}
        <div style={{ ...sectionLbl, marginTop: '14px', marginBottom: '0' }}>CAPITAL</div>
        <div style={colGrid}>
          <div style={colCell}>
            <RiskTooltip label="Available" color={V.gold} explanation="Your available trading capital. All risk limits are calculated from this baseline. See Current Exposure below for how much of this capital is actively at risk in open positions." />
            <div style={{ fontSize: '18px', fontWeight: 700, color: V.gold, fontFamily: "'Jost',sans-serif", lineHeight: 1.1 }}>{fmtAbbrev(rsBaseline)}</div>
          </div>
          <div style={colCell}>
            <RiskTooltip label="Floor" color={V.sage} explanation="The lowest your account can go before you stop trading for the month. Protects you from a catastrophic drawdown." />
            <div style={{ fontSize: '18px', fontWeight: 700, color: V.sage, fontFamily: "'Jost',sans-serif", lineHeight: 1.1 }}>{fmtAbbrev(rsFloor)}</div>
          </div>
          <div style={colCell}>
            <RiskTooltip label="Buffer" color={bufferColor} explanation="The distance between your available capital and your floor. Green = healthy room. Yellow = getting close. Red = near your limit — trade with extra care." />
            <div style={{ fontSize: '18px', fontWeight: 700, color: bufferColor, fontFamily: "'Jost',sans-serif", lineHeight: 1.1 }}>{fmtAbbrev(buffer)}</div>
          </div>
        </div>
        {/* Divider */}
        <div style={{ borderTop: '0.5px solid rgba(201,168,76,0.15)', marginTop: '16px', marginBottom: '14px' }} />
        {/* Section 2 — Today's Limits */}
        <div style={{ ...sectionLbl, marginBottom: '0' }}>TODAY'S LIMITS</div>
        <div style={colGrid}>
          <div style={colCell}>
            <RiskTooltip label="Daily Stop" color='#e53935' explanation="The maximum dollar loss allowed in a single day. If you hit this number, close all positions and stop trading until tomorrow." />
            <div style={{ fontSize: '18px', fontWeight: 700, color: '#e53935', fontFamily: "'Jost',sans-serif", lineHeight: 1.1 }}>{fmtAbbrev(dailyStop)}</div>
          </div>
          <div style={colCell}>
            <RiskTooltip label="Per Trade" color={V.gold} explanation="The maximum you can risk on any single trade. Keeps one bad position from derailing your whole account." />
            <div style={{ fontSize: '18px', fontWeight: 700, color: V.gold, fontFamily: "'Jost',sans-serif", lineHeight: 1.1 }}>{fmtAbbrev(perTradeLimit)}</div>
          </div>
          <div style={colCell}>
            <RiskTooltip label="Weekly Stop" color={V.gold} explanation="The maximum cumulative loss allowed across the full trading week. Hit this and you sit out until Monday — no exceptions." />
            <div style={{ fontSize: '18px', fontWeight: 700, color: V.gold, fontFamily: "'Jost',sans-serif", lineHeight: 1.1 }}>{fmtAbbrev(weeklyStop)}</div>
          </div>
        </div>
        {/* Section 3 — Current Exposure */}
        <div style={{ borderTop: '0.5px solid rgba(201,168,76,0.15)', marginTop: '14px', marginBottom: '14px' }} />
        <div style={{ ...sectionLbl, marginBottom: '0' }}>CURRENT EXPOSURE</div>
        {rsOpenTrades.length === 0 ? (
          <div style={{ marginTop: '8px' }}>
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted, fontStyle: 'italic', lineHeight: 1.6 }}>No open positions logged. Add trades to your Trading Journal to track live exposure.</div>
            <button onClick={() => onNavigate && onNavigate('journal')} style={{ marginTop: '6px', background: 'none', border: `0.5px solid ${V.border}`, borderRadius: '6px', padding: '4px 10px', color: V.muted, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: '11px' }}>Open Trading Journal →</button>
          </div>
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '8px' }}>
              <div style={colCell}>
                <RiskTooltip label="Capital at Risk" color={expColor} explanation="Total capital currently at risk across all open positions in your Trading Journal. Updates automatically as you log trades." />
                <div style={{ fontSize: '18px', fontWeight: 700, color: expColor, fontFamily: "'Jost',sans-serif", lineHeight: 1.1 }}>{fmtAbbrev(totalAtRisk)}</div>
              </div>
              <div style={colCell}>
                <RiskTooltip label="% of Baseline" color={expColor} explanation="Your total open risk as a percentage of your available capital. Stay below your per-trade limit on each position and your daily max loss overall." />
                <div style={{ fontSize: '18px', fontWeight: 700, color: expColor, fontFamily: "'Jost',sans-serif", lineHeight: 1.1 }}>{riskPct.toFixed(1)}%</div>
              </div>
            </div>
            {/* Exposure meter */}
            <div style={{ background: 'rgba(240,237,230,0.1)', borderRadius: '20px', height: '6px', width: '100%', marginTop: '10px', overflow: 'hidden' }}>
              <div style={{ width: `${meterFill}%`, height: '6px', borderRadius: '20px', background: expColor, transition: 'width 0.4s ease' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
              <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.muted }}>0</span>
              <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.muted }}>{riskPct.toFixed(1)}% of {rsDailyMax}% daily limit used</span>
              <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.muted }}>Max: {fmtAbbrev(dailyStop)}</span>
            </div>
          </div>
        )}
        {/* Color code legend */}
        <div style={{ display: 'flex', gap: '14px', marginTop: '12px', alignItems: 'center' }}>
          {[
            { color: V.sage,    label: 'Healthy: under 2%' },
            { color: V.gold,    label: 'Monitor: 2–5%' },
            { color: '#e53935', label: 'Stop: over 5%' },
          ].map(k => (
            <div key={k.label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: k.color, flexShrink: 0 }} />
              <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.dim }}>{k.label}</span>
            </div>
          ))}
        </div>
        {/* Update button + saved confirmation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '14px' }}>
          <button
            onClick={() => showRiskEdit ? setShowRiskEdit(false) : openRscEdit()}
            style={{ background: 'none', border: `0.5px solid ${V.border}`, borderRadius: '6px', padding: '5px 12px', color: V.muted, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: '11px', letterSpacing: '0.04em' }}>
            {showRiskEdit ? '✕ Cancel' : '✎ Update My Numbers'}
          </button>
          {rscSaved && <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.sage }}>✓ Numbers updated</span>}
        </div>

        {/* Inline edit panel */}
        {showRiskEdit && (
          <div style={{ borderTop: '0.5px solid rgba(201,168,76,0.2)', paddingTop: '16px', marginTop: '12px' }}>
            <div style={{ fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase', color: V.gold, fontFamily: "'Jost',sans-serif", marginBottom: '14px' }}>UPDATE YOUR NUMBERS</div>

            {/* Account type */}
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted, marginBottom: '6px' }}>Account Type</div>
              <div style={{ display: 'flex', gap: '6px' }}>
                {[{ v: 'cash', l: 'Cash' }, { v: 'margin', l: 'Margin' }, { v: 'portfolio', l: 'Portfolio' }].map(o => (
                  <button key={o.v} onClick={() => setEditAcctType(o.v)}
                    style={{ padding: '5px 12px', borderRadius: '6px', border: `0.5px solid ${editAcctType === o.v ? 'rgba(201,168,76,0.5)' : V.border}`, background: editAcctType === o.v ? 'rgba(201,168,76,0.15)' : 'transparent', color: editAcctType === o.v ? V.goldL : V.muted, fontFamily: "'Jost',sans-serif", fontSize: '11px', cursor: 'pointer' }}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>

            {/* Baseline */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted, marginBottom: '4px' }}>
                {editAcctType === 'margin' ? 'Options Buying Power ($)' : editAcctType === 'portfolio' ? 'Available Dollars ($)' : 'Available Cash ($)'}
              </div>
              <input type="number" value={editBaseline} onChange={e => setEditBaseline(e.target.value)}
                style={{ width: '100%', background: V.bg3, border: `0.5px solid ${V.border}`, borderRadius: '6px', padding: '8px 10px', color: V.text, fontFamily: "'Jost',sans-serif", fontSize: '13px', boxSizing: 'border-box' }} />
            </div>

            {/* Percent fields 2-col grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
              {[
                { val: editPerTrade,  set: setEditPerTrade,  label: 'Max Risk Per Trade (%)' },
                { val: editDailyMax,  set: setEditDailyMax,  label: 'Daily Max Loss (%)'     },
                { val: editWeeklyMax, set: setEditWeeklyMax, label: 'Weekly Max Loss (%)'    },
              ].map(f => {
                const pct   = parseFloat(f.val) || 0;
                const bl    = parseFloat(editBaseline) || 0;
                const equiv = bl > 0 && pct > 0 ? `= $${Math.round(bl * pct / 100).toLocaleString()}` : '';
                return (
                  <div key={f.label}>
                    <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted, marginBottom: '4px' }}>{f.label}</div>
                    <input type="number" value={f.val} onChange={e => f.set(e.target.value)}
                      style={{ width: '100%', background: V.bg3, border: `0.5px solid ${V.border}`, borderRadius: '6px', padding: '8px 10px', color: V.text, fontFamily: "'Jost',sans-serif", fontSize: '13px', boxSizing: 'border-box' }} />
                    {equiv && <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.dim, marginTop: '3px' }}>{equiv}</div>}
                  </div>
                );
              })}
              <div>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted, marginBottom: '4px' }}>Personal Floor ($)</div>
                <input type="number" value={editFloor} onChange={e => setEditFloor(e.target.value)}
                  style={{ width: '100%', background: V.bg3, border: `0.5px solid ${V.border}`, borderRadius: '6px', padding: '8px 10px', color: V.text, fontFamily: "'Jost',sans-serif", fontSize: '13px', boxSizing: 'border-box' }} />
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.dim, marginTop: '3px' }}>The minimum balance you will never trade below.</div>
              </div>
            </div>

            {/* Save / Cancel */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <button onClick={saveRscEdit}
                style={{ padding: '9px 20px', background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: '8px', color: V.goldL, fontFamily: "'Jost',sans-serif", fontSize: '12px', letterSpacing: '0.06em', cursor: 'pointer', fontWeight: 500 }}>
                Save →
              </button>
              <button onClick={() => setShowRiskEdit(false)}
                style={{ padding: '9px 16px', background: 'none', border: `0.5px solid ${V.border}`, borderRadius: '8px', color: V.muted, fontFamily: "'Jost',sans-serif", fontSize: '12px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ fontSize: '10px', color: V.dim, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', marginTop: '10px' }}>{acctLabel} account · limits from your Risk Dashboard</div>
        {hasTodayLog && (
          <div style={{ fontSize: '10px', color: V.sage, fontFamily: "'Jost',sans-serif", marginTop: '4px' }}>
            ✓ Today logged — {todayPnl >= 0 ? '+' : ''}{todayPnl >= 0 ? '$' : '-$'}{Math.round(Math.abs(todayPnl)).toLocaleString()} {todayPnl >= 0 ? '↑' : '↓'}
          </div>
        )}
      </div>
    );
  })();

  const whyCard = (
    <div style={{ ...cardBase, border: `0.5px solid rgba(201,168,76,0.25)`, marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
        <div style={{ ...serif, fontSize: '20px', color: V.gold, fontWeight: 400 }}>Why I Trade</div>
        {whysSet && !editingWhys && (
          <button style={{ ...btnGhost, padding: '4px 12px', fontSize: '11px' }} onClick={() => { setWhyInputs([..._savedWhys, '', ''].slice(0, 3)); setEditingWhys(true); }}>Edit</button>
        )}
      </div>
      {(!whysSet || editingWhys) ? (
        <div>
          {!editingWhys && <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost', sans-serif", fontStyle: 'italic', lineHeight: 1.7, marginBottom: '16px' }}>Your top 3 reasons will appear here every day — a reminder of what you are building toward.</p>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
            {[0, 1, 2].map(i => (
              <input key={i} placeholder={i === 0 ? 'e.g. Replace my income' : `Why #${i + 1}`} value={whyInputs[i] || ''} onChange={e => handleWhyChange(i, e.target.value)} style={inp} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button style={{ ...btnGold, padding: '10px 24px', fontSize: '13px', minHeight: '44px' }} onClick={saveWhys}>Save My Why →</button>
            {editingWhys && <button style={{ ...btnGhost, padding: '10px 16px', fontSize: '12px', minHeight: '44px' }} onClick={() => setEditingWhys(false)}>Cancel</button>}
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
            {_savedWhys.map((w, i) => (
              <div key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                <span style={{ ...serif, fontSize: '28px', color: V.dim, lineHeight: 1.1, flexShrink: 0, fontWeight: 400 }}>{i + 1}.</span>
                <p style={{ ...serif, fontSize: '18px', color: V.gold, fontStyle: 'italic', lineHeight: 1.5, margin: 0 }}>{w}</p>
              </div>
            ))}
          </div>
          <p style={{ ...serif, fontSize: '13px', color: V.dim, fontStyle: 'italic', lineHeight: 1.7, margin: 0, textAlign: 'center' }}>
            "It does not matter how slowly you go as long as you do not stop." — Confucius
          </p>
        </div>
      )}
    </div>
  );

  const whyPromptCard = !whysSet ? (
    <div style={{ background: V.bg2, border: `1px solid rgba(201,168,76,0.4)`, borderRadius: '8px', padding: '14px', marginBottom: '12px' }}>
      <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.gold }}>✎ You haven't written your WHY yet.</div>
      <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted, marginTop: '4px', lineHeight: 1.7 }}>It takes two minutes and it will greet you every morning. What are you really trading for?</div>
    </div>
  ) : null;

  const goalsCard = (
    <div style={{ ...cardBase, marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
        <div style={{ ...serif, fontSize: '20px', color: V.gold, fontWeight: 400 }}>My Goals / The Bigger Picture</div>
        {goals.length > 0 && !addingGoal && (
          <button style={{ ...btnGold, padding: '5px 14px', fontSize: '11px' }} onClick={() => setAddingGoal(true)}>Add Another Goal +</button>
        )}
      </div>
      {goals.length === 0 && !addingGoal && (
        <div>
          <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost', sans-serif", lineHeight: 1.7, marginBottom: '16px' }}>
            What are you working toward beyond trading? Add your goals and track your progress here.
          </p>
          <button style={{ ...btnGold, padding: '10px 24px', fontSize: '13px', minHeight: '44px' }} onClick={() => setAddingGoal(true)}>Add a Goal →</button>
        </div>
      )}
      {goals.filter(g => g.showOnDashboard !== false).map((g, idx) => {
        const pct = g.targetAmount > 0 ? Math.min(Math.round((g.currentAmount / g.targetAmount) * 100), 100) : 0;
        return editGoalIdx === idx ? (
          <div key={g.id || idx} style={{ borderTop: `0.5px solid ${V.border}`, paddingTop: '14px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
              <input value={editGoalForm.name || ''} onChange={e => setEditGoalForm(f => ({ ...f, name: e.target.value }))} placeholder="Goal name" style={inp} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <input type="number" value={editGoalForm.targetAmount || ''} onChange={e => setEditGoalForm(f => ({ ...f, targetAmount: e.target.value }))} placeholder="Target ($)" style={inp} />
                <input type="date" value={editGoalForm.targetDate || ''} onChange={e => setEditGoalForm(f => ({ ...f, targetDate: e.target.value }))} style={{ ...inp, colorScheme: 'dark' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={{ ...btnGold, padding: '8px 18px', fontSize: '12px', minHeight: '44px' }} onClick={saveEditGoal}>Save</button>
              <button style={{ ...btnGhost, padding: '8px 14px', fontSize: '12px', minHeight: '44px' }} onClick={() => { setEditGoalIdx(null); setDeleteConfirmId(null); }}>Cancel</button>
            </div>
            {deleteConfirmId === (g.id || idx) ? (
              <div style={{ marginTop: '14px', padding: '14px', background: 'rgba(196,122,122,0.06)', border: '0.5px solid rgba(196,122,122,0.25)', borderRadius: '8px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: 'rgba(240,237,230,0.7)', lineHeight: 1.6, marginBottom: '12px' }}>
                  Are you sure you want to delete this goal?<br />This cannot be undone.
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button style={{ padding: '8px 16px', background: 'rgba(196,122,122,0.12)', border: '1px solid rgba(196,122,122,0.4)', borderRadius: '8px', color: V.rose, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: '12px', minHeight: '44px' }} onClick={() => deleteGoal(g.id || idx)}>Yes, delete it</button>
                  <button style={{ ...btnGhost, padding: '8px 14px', fontSize: '12px', minHeight: '44px' }} onClick={() => setDeleteConfirmId(null)}>Keep it</button>
                </div>
              </div>
            ) : (
              <button style={{ background: 'none', border: 'none', color: V.rose, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: '12px', padding: 0, marginTop: '12px', letterSpacing: '0.02em' }} onClick={() => setDeleteConfirmId(g.id || idx)}>Delete this goal</button>
            )}
          </div>
        ) : (
          <div key={g.id || idx} style={{ borderTop: `0.5px solid ${V.border}`, paddingTop: '14px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
              <div style={{ ...serif, fontSize: '17px', color: V.gold }}>{g.name}</div>
              <button style={{ ...btnGhost, padding: '3px 10px', fontSize: '10px' }} onClick={() => { setEditGoalIdx(idx); setEditGoalForm({ name: g.name, targetAmount: g.targetAmount, targetDate: g.targetDate }); }}>Edit</button>
            </div>
            {g.startingAmount > 0 && (
              <div style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost',sans-serif", marginBottom: '8px' }}>
                Started with <span style={{ ...mono, color: V.muted }}>${g.startingAmount.toLocaleString()}</span>
              </div>
            )}
            {g.targetAmount > 0 && (
              <>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>
                  <div style={{ flex: 1, height: '5px', background: 'rgba(240,237,230,0.07)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg,${V.gold},${V.goldL})`, borderRadius: '3px', transition: 'width 0.4s ease' }} />
                  </div>
                  <span style={{ ...mono, fontSize: '11px', color: V.gold, flexShrink: 0 }}>{pct}%</span>
                </div>
                <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: V.dim, fontFamily: "'Jost', sans-serif", marginBottom: '8px' }}>
                  <span>Saved: <span style={{ color: V.muted, ...mono }}>${(g.currentAmount || 0).toLocaleString()}</span></span>
                  <span>Target: <span style={{ color: V.muted, ...mono }}>${g.targetAmount.toLocaleString()}</span></span>
                  {g.projectedDate && <span>Est: <span style={{ color: V.muted }}>{g.projectedDate}</span></span>}
                  {g.targetDate && !g.projectedDate && <span>By: <span style={{ color: V.muted }}>{new Date(g.targetDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span></span>}
                </div>
                {(() => {
                  const isGoalMet = (g.currentAmount || 0) >= g.targetAmount;
                  if (isGoalMet) return <div style={{ fontSize: '12px', color: V.sage, fontFamily: "'Jost',sans-serif", marginBottom: '8px', fontWeight: 500 }}>✓ Goal reached</div>;
                  const isTargetDateGoal = g.contributionType === 'targetDate' || g.calcMode === 'date';
                  if (isTargetDateGoal && g.targetDate) {
                    const targetD = new Date(g.targetDate + 'T00:00:00');
                    if (targetD < now) return <div style={{ fontSize: '11px', color: V.rose, fontFamily: "'Jost',sans-serif", marginBottom: '8px' }}>Target date passed — update your goal</div>;
                    const rawMonths = (targetD.getFullYear() - now.getFullYear()) * 12 + (targetD.getMonth() - now.getMonth());
                    const monthsLeft = Math.max(1, rawMonths);
                    const remaining = Math.max(0, g.targetAmount - (g.currentAmount || 0));
                    const liveMonthly = Math.ceil(remaining / monthsLeft);
                    const pillStyle = { background: 'rgba(201,168,76,0.1)', border: '0.5px solid rgba(201,168,76,0.3)', borderRadius: '20px', padding: '3px 10px', color: V.sage, fontFamily: "'Jost',sans-serif", fontSize: '12px', display: 'inline-block', marginTop: '6px' };
                    return (
                      <div style={{ marginBottom: '10px' }}>
                        <span style={pillStyle}>{monthsLeft === 1 ? `$${liveMonthly.toLocaleString()} needed this month` : `$${liveMonthly.toLocaleString()}/month needed`}</span>
                        <span style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', marginLeft: '8px' }}>{monthsLeft} month{monthsLeft !== 1 ? 's' : ''} to go</span>
                      </div>
                    );
                  }
                  if ((g.contributionType === 'monthly' || g.calcMode === 'contribution') && g.monthlyAmount > 0) return (
                    <div style={{ marginBottom: '10px' }}>
                      <span style={{ background: 'rgba(201,168,76,0.1)', border: '0.5px solid rgba(201,168,76,0.3)', borderRadius: '20px', padding: '3px 10px', color: V.sage, fontFamily: "'Jost',sans-serif", fontSize: '12px', display: 'inline-block', marginTop: '6px' }}>${g.monthlyAmount.toLocaleString()}/month committed</span>
                    </div>
                  );
                  return null;
                })()}
              </>
            )}
            {updateProgressIdx === idx ? (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                <input type="number" placeholder="Amount to add ($)" value={updateProgressAmount} onChange={e => setUpdateProgressAmount(e.target.value)} style={{ ...inp, flex: 1 }} />
                <button style={{ ...btnGold, padding: '8px 14px', fontSize: '12px', minHeight: '44px' }} onClick={() => saveProgress(idx)}>Save</button>
                <button style={{ ...btnGhost, padding: '8px 10px', fontSize: '12px', minHeight: '44px' }} onClick={() => { setUpdateProgressIdx(null); setUpdateProgressAmount(''); }}>✕</button>
              </div>
            ) : (
              <button style={{ ...btnGhost, padding: '7px 12px', fontSize: '11px', marginTop: '4px', minHeight: '44px' }} onClick={() => { setUpdateProgressIdx(idx); setUpdateProgressAmount(String(g.currentAmount || '')); }}>Update Progress →</button>
            )}
          </div>
        );
      })}
      {addingGoal && (
        <div style={{ borderTop: goals.length > 0 ? `0.5px solid ${V.border}` : 'none', paddingTop: goals.length > 0 ? '14px' : 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
            <input placeholder="e.g. New car, college fund, freedom fund" value={goalForm.name} onChange={e => setGoalForm(f => ({ ...f, name: e.target.value }))} style={inp} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <input type="number" placeholder="Starting amount ($)" value={goalForm.startingAmount} onChange={e => setGoalForm(f => ({ ...f, startingAmount: e.target.value }))} style={inp} />
              <input type="number" placeholder="Target amount ($)" value={goalForm.targetAmount} onChange={e => setGoalForm(f => ({ ...f, targetAmount: e.target.value }))} style={inp} />
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {[{ v: 'contribution', label: 'I know my monthly contribution' }, { v: 'date', label: 'I know my target date' }].map(({ v, label }) => (
                <button key={v} onClick={() => setGoalForm(f => ({ ...f, calcMode: v }))} style={{ flex: 1, padding: '8px 12px', background: goalForm.calcMode === v ? 'rgba(201,168,76,0.12)' : V.bg3, border: `0.5px solid ${goalForm.calcMode === v ? V.gold : V.border}`, borderRadius: '8px', color: goalForm.calcMode === v ? V.gold : V.muted, fontSize: '12px', cursor: 'pointer', fontFamily: "'Jost', sans-serif" }}>{label}</button>
              ))}
            </div>
            {goalForm.calcMode === 'contribution' ? (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="number" placeholder="Monthly contribution ($)" value={goalForm.monthlyContribution} onChange={e => setGoalForm(f => ({ ...f, monthlyContribution: e.target.value }))} style={{ ...inp, flex: 1 }} />
                {goalForm.monthlyContribution && goalForm.targetAmount && (
                  <div style={{ fontSize: '12px', color: V.sage, fontFamily: "'Jost', sans-serif", flexShrink: 0 }}>
                    Est: {computeProjected(goalForm.startingAmount, goalForm.targetAmount, goalForm.monthlyContribution) || '—'}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="date" value={goalForm.targetDate} onChange={e => setGoalForm(f => ({ ...f, targetDate: e.target.value }))} style={{ ...inp, flex: 1, colorScheme: 'dark' }} />
                {goalForm.targetDate && goalForm.targetAmount && (
                  <div style={{ fontSize: '12px', color: V.sage, fontFamily: "'Jost', sans-serif", flexShrink: 0 }}>
                    ${(computeMonthlyNeeded(goalForm.startingAmount, goalForm.targetAmount, goalForm.targetDate) || '—').toLocaleString()}/mo needed
                  </div>
                )}
              </div>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: V.muted, fontFamily: "'Jost', sans-serif", cursor: 'pointer' }}>
              <input type="checkbox" checked={goalForm.showOnDashboard} onChange={e => setGoalForm(f => ({ ...f, showOnDashboard: e.target.checked }))} />
              Show on dashboard
            </label>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{ ...btnGold, padding: '9px 22px', fontSize: '12px', minHeight: '44px' }} onClick={saveNewGoal}>Save Goal →</button>
            <button style={{ ...btnGhost, padding: '9px 16px', fontSize: '12px', minHeight: '44px' }} onClick={() => setAddingGoal(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );

  const goalsPromptCard = goals.length === 0 && !addingGoal ? (
    <div style={{ background: V.bg2, border: `1px solid rgba(201,168,76,0.4)`, borderRadius: '8px', padding: '14px', marginBottom: '12px' }}>
      <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.gold }}>✎ You haven't set your goals yet.</div>
      <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted, marginTop: '4px', lineHeight: 1.7 }}>Where do you want to be in 3 months? 6 months? A year? Write it down. Make it real.</div>
      <button style={{ ...btnGold, padding: '6px 14px', fontSize: '11px', marginTop: '8px', minHeight: '36px' }} onClick={() => setAddingGoal(true)}>Set My Goals →</button>
    </div>
  ) : null;

  const communityCard = (
    <div style={{ ...cardBase, textAlign: 'center', padding: '28px 24px', marginBottom: '20px' }}>
      <div style={{ ...serif, fontSize: '22px', color: V.gold, marginBottom: '12px', fontWeight: 400 }}>The Trade Like a Woman Community</div>
      <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost', sans-serif", maxWidth: '560px', margin: '0 auto 22px', lineHeight: 1.8 }}>
        You don't have to do this alone. Ask questions, share wins, and learn alongside women on the same path.
      </p>
      <button style={{ ...btnGold, padding: '12px 32px', fontSize: '13px', minHeight: '44px' }}
        onClick={() => window.open('https://www.facebook.com/groups/1519037119571290/members', '_blank')}>
        Join the Conversation →
      </button>
    </div>
  );

  // ── Capital at Risk — two variants ──────────────────────────────

  const riskStatus = capitalAtRisk === 0
    ? 'No open positions. Your capital is fully protected.'
    : riskPct < 2 ? 'Your capital is protected. You are trading within your plan.'
    : riskPct < 5 ? 'Approaching your risk threshold. Stay alert and review your open positions.'
    : 'Capital at elevated risk. Review your open positions immediately.';

  const riskStatusShort = capitalAtRisk === 0
    ? 'Capital fully protected.'
    : riskPct < 2 ? 'Trading within your plan.'
    : riskPct < 5 ? 'Approaching threshold. Stay alert.'
    : 'Elevated risk. Review positions now.';

  const riskTableRows = [
    { heading: 'Portfolio at risk', rows: [['🟢','Under 2%','Protected'],['🟡','2%–5%','Monitor'],['🔴','Over 5%','Reduce']] },
    { heading: 'Per trade',         rows: [['🟢','Under 1%','Ideal'],['🟡','1%–2%','OK'],['🔴','Over 2%','Resize']] },
  ];

  const capitalMobileCard = (
    <div style={{ ...cardBase, marginBottom: '16px' }}>
      <div style={{ fontSize: '11px', letterSpacing: '0.3em', textTransform: 'uppercase', color: V.muted, fontFamily: "'Jost', sans-serif", marginBottom: '16px' }}>Capital at Risk</div>
      {accountSize === 0 ? (
        <div>
          <div style={{ fontFamily: "'Jost', sans-serif", fontSize: '36px', fontWeight: 700, color: V.sage, lineHeight: 1, marginBottom: '8px' }}>$0.00</div>
          <div style={{ fontSize: '13px', color: V.sage, fontFamily: "'Jost', sans-serif", marginBottom: '14px' }}>No open positions. Your capital is fully protected.</div>
          <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost', sans-serif", marginBottom: '14px' }}>Set your account size to see your risk percentage.</p>
          <button style={{ ...btnGold, width: '100%', padding: '13px', fontSize: '14px', minHeight: '48px', position: 'relative', zIndex: 10, pointerEvents: 'auto', cursor: 'pointer' }} onClick={() => { console.log('Set Account Size clicked'); onNavigate('config'); }}>Set Account Size →</button>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '14px', marginBottom: '10px' }}>
            <div style={{ fontFamily: "'Jost', sans-serif", fontSize: '36px', fontWeight: 700, color: riskColor, lineHeight: 1 }}>${capitalAtRisk.toLocaleString()}</div>
            {riskPct !== null && <div style={{ fontFamily: "'Jost', sans-serif", fontSize: '20px', fontWeight: 700, color: riskColor, paddingBottom: '3px', lineHeight: 1 }}>{riskPct.toFixed(1)}%</div>}
          </div>
          <div style={{ height: '6px', background: 'rgba(240,237,230,0.07)', borderRadius: '3px', overflow: 'hidden', marginBottom: '10px' }}>
            <div style={{ height: '100%', width: `${Math.min((riskPct || 0) / 10 * 100, 100)}%`, background: riskColor, borderRadius: '3px', transition: 'width 0.4s ease' }} />
          </div>
          <div style={{ fontSize: '13px', color: riskColor, fontFamily: "'Jost', sans-serif", marginBottom: '8px' }}>{riskStatus}</div>
          <p style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost', sans-serif", fontStyle: 'italic', margin: '0 0 5px', lineHeight: 1.6 }}>
            Based on 2x premium rule. Enter current marks in Profit & Loss for real-time accuracy.
          </p>
          {theoreticalMaxLoss > capitalAtRisk && (
            <p style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost', sans-serif", margin: '0 0 5px', lineHeight: 1.6 }}>
              Theoretical max loss across all open positions: ${theoreticalMaxLoss.toLocaleString()}
            </p>
          )}
          {anyOpenWithoutMark && openTrades.length > 0 && (
            <p style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost', sans-serif", fontStyle: 'italic', margin: '0 0 12px', lineHeight: 1.6 }}>
              Some positions may have decayed significantly. Your actual risk is likely lower than shown.{' '}
              <span onClick={() => onNavigate('pnl')} style={{ color: V.gold, cursor: 'pointer', textDecoration: 'underline' }}>Update marks →</span>
            </p>
          )}
        </div>
      )}
      <div style={{ background: V.bg3, border: `0.5px solid rgba(201,168,76,0.15)`, borderRadius: '8px', padding: '12px 14px', marginTop: '4px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase', color: V.muted, fontFamily: "'Jost', sans-serif" }}>Healthy Risk Levels</div>
          <button style={{ background: 'transparent', border: 'none', color: V.muted, fontSize: '11px', cursor: 'pointer', fontFamily: "'Jost', sans-serif", padding: '2px 0', minHeight: '44px' }} onClick={() => setShowRiskDetails(d => !d)}>
            {showRiskDetails ? 'Hide ▴' : 'See full guidelines →'}
          </button>
        </div>
        {showRiskDetails && (
          <div style={{ marginTop: '10px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              {riskTableRows.map(({ heading, rows }) => (
                <div key={heading}>
                  <div style={{ fontSize: '10px', color: V.dim, fontFamily: "'Jost', sans-serif", marginBottom: '5px' }}>{heading}</div>
                  {rows.map(([dot, range, label]) => (
                    <div key={range} style={{ fontSize: '11px', color: V.muted, fontFamily: "'Jost', sans-serif", marginBottom: '3px' }}>{dot} {range} <span style={{ color: V.dim }}>— {label}</span></div>
                  ))}
                </div>
              ))}
            </div>
            <p style={{ ...serif, fontSize: '11px', color: V.dim, fontStyle: 'italic', lineHeight: 1.6, margin: 0 }}>
              "The first rule is not to lose. The second rule is not to forget the first rule." — Warren Buffett
            </p>
          </div>
        )}
      </div>
      <p style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost', sans-serif", fontStyle: 'italic', marginTop: '10px', lineHeight: 1.6 }}>
        Includes open CSP and spread positions. Covered calls excluded — hedged by existing share holdings.
      </p>
      {accountType === 'margin' && (
        <p style={{ fontSize: '11px', color: V.gold, fontFamily: "'Jost', sans-serif", marginTop: '6px', lineHeight: 1.6 }}>
          Using Net Liquidation Value for risk calculations.
        </p>
      )}
    </div>
  );

  const capitalDesktopCard = (
    <div style={{ ...cardBase, marginBottom: '16px', position: 'relative', zIndex: 1 }}>
      <div style={{ fontSize: '11px', letterSpacing: '0.3em', textTransform: 'uppercase', color: V.muted, fontFamily: "'Jost', sans-serif", marginBottom: '14px' }}>Capital at Risk</div>
      {accountSize === 0 ? (
        <div>
          <div style={{ fontFamily: "'Jost', sans-serif", fontSize: '38px', fontWeight: 700, color: V.sage, lineHeight: 1, marginBottom: '6px' }}>$0.00</div>
          <div style={{ fontSize: '13px', color: V.sage, fontFamily: "'Jost', sans-serif", marginBottom: '14px' }}>No open positions. Capital fully protected.</div>
          <p style={{ fontSize: '12px', color: V.muted, fontFamily: "'Jost', sans-serif", marginBottom: '12px' }}>Set your account size to see your risk percentage.</p>
          <button style={{ ...btnGold, padding: '9px 16px', fontSize: '12px', width: '100%', minHeight: '44px', position: 'relative', zIndex: 10, pointerEvents: 'auto', cursor: 'pointer' }} onClick={() => { console.log('Set Account Size clicked'); onNavigate('config'); }}>Set Account Size →</button>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', marginBottom: '10px' }}>
            <div style={{ fontFamily: "'Jost', sans-serif", fontSize: '38px', fontWeight: 700, color: riskColor, lineHeight: 1 }}>${capitalAtRisk.toLocaleString()}</div>
            {riskPct !== null && <div style={{ fontFamily: "'Jost', sans-serif", fontSize: '22px', fontWeight: 700, color: riskColor, paddingBottom: '2px', lineHeight: 1 }}>{riskPct.toFixed(1)}%</div>}
          </div>
          <div style={{ height: '5px', background: 'rgba(240,237,230,0.07)', borderRadius: '3px', overflow: 'hidden', marginBottom: '10px' }}>
            <div style={{ height: '100%', width: `${Math.min((riskPct || 0) / 10 * 100, 100)}%`, background: riskColor, borderRadius: '3px', transition: 'width 0.4s ease' }} />
          </div>
          <div style={{ fontSize: '12px', color: riskColor, fontFamily: "'Jost', sans-serif", marginBottom: '8px' }}>{riskStatusShort}</div>
          <p style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost', sans-serif", fontStyle: 'italic', margin: '0 0 5px', lineHeight: 1.6 }}>
            Based on 2x premium rule. Enter current marks in Profit & Loss for real-time accuracy.
          </p>
          {theoreticalMaxLoss > capitalAtRisk && (
            <p style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost', sans-serif", margin: '0 0 5px', lineHeight: 1.6 }}>
              Theoretical max loss across all open positions: ${theoreticalMaxLoss.toLocaleString()}
            </p>
          )}
          {anyOpenWithoutMark && openTrades.length > 0 && (
            <p style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost', sans-serif", fontStyle: 'italic', margin: '0 0 12px', lineHeight: 1.6 }}>
              Some positions may have decayed significantly. Your actual risk is likely lower than shown.{' '}
              <span onClick={() => onNavigate('pnl')} style={{ color: V.gold, cursor: 'pointer', textDecoration: 'underline' }}>Update marks →</span>
            </p>
          )}
        </div>
      )}
      <div style={{ background: V.bg3, border: `0.5px solid rgba(201,168,76,0.15)`, borderRadius: '8px', padding: '12px 14px', marginTop: '4px' }}>
        <div style={{ fontSize: '9px', letterSpacing: '0.15em', textTransform: 'uppercase', color: V.muted, fontFamily: "'Jost', sans-serif", marginBottom: '8px' }}>Healthy Risk Levels</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '8px' }}>
          {riskTableRows.map(({ heading, rows }) => (
            <div key={heading}>
              <div style={{ fontSize: '10px', color: V.dim, fontFamily: "'Jost', sans-serif", marginBottom: '4px' }}>{heading}</div>
              {rows.map(([dot, range, label]) => (
                <div key={range} style={{ fontSize: '11px', color: V.muted, fontFamily: "'Jost', sans-serif", marginBottom: '3px' }}>{dot} {range} <span style={{ color: V.dim }}>— {label}</span></div>
              ))}
            </div>
          ))}
        </div>
        <p style={{ ...serif, fontSize: '11px', color: V.dim, fontStyle: 'italic', lineHeight: 1.6, margin: 0 }}>
          "The first rule is not to lose." — Warren Buffett
        </p>
      </div>
      <p style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost', sans-serif", fontStyle: 'italic', marginTop: '10px', lineHeight: 1.6 }}>
        Includes open CSP and spread positions. Covered calls excluded — hedged by existing share holdings.
      </p>
      {accountType === 'margin' && (
        <p style={{ fontSize: '11px', color: V.gold, fontFamily: "'Jost', sans-serif", marginTop: '6px', lineHeight: 1.6 }}>
          Using Net Liquidation Value for risk calculations.
        </p>
      )}
    </div>
  );

  const tradesCard = (
    <div style={{ ...cardBase, border: `0.5px solid rgba(201,168,76,0.12)`, marginBottom: '16px' }}>
      <div style={{ fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: V.muted, fontFamily: "'Jost', sans-serif", marginBottom: '12px' }}>Your Trades</div>
      <div style={{ marginBottom: '12px' }}>
        <div style={{ ...mono, fontSize: '28px', fontWeight: 500, color: V.text, lineHeight: 1, marginBottom: '4px' }}>{trades.length}</div>
        <div style={{ fontSize: '11px', color: V.muted, marginBottom: '8px', fontFamily: "'Jost', sans-serif" }}>trades logged</div>
        {winRate !== null ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <div style={{ height: '4px', flex: 1, background: 'rgba(240,237,230,0.07)', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${winRate}%`, background: `linear-gradient(90deg,${V.sage},#9dc4aa)`, borderRadius: '2px' }} />
            </div>
            <span style={{ ...mono, fontSize: '12px', color: V.sage, flexShrink: 0 }}>{winRate}% win</span>
          </div>
        ) : (
          <div style={{ fontSize: '11px', color: V.dim, fontStyle: 'italic', fontFamily: "'Jost', sans-serif" }}>{trades.length === 0 ? 'No trades logged yet.' : 'Close 2+ trades for win rate.'}</div>
        )}
        {lastTradeDate && <div style={{ fontSize: '10px', color: V.dim, fontFamily: "'Jost', sans-serif" }}>Last: {lastTradeDate}</div>}
      </div>
      <button style={{ ...btnGold, width: '100%', padding: '10px', fontSize: '12px', textAlign: 'center', minHeight: '44px' }} onClick={() => onNavigate('pnl')}>View Profit & Loss →</button>
    </div>
  );

  const learningJourneyCard = (() => {
    const allFeedback = (() => { try { return JSON.parse(localStorage.getItem('tlaw_feedback') || '[]'); } catch { return []; } })();
    const submitted = allFeedback.filter(e => !e.skipped);
    if (submitted.length === 0) return null;
    const chaptersDone = new Set(submitted.filter(e => e.type === 'chapter').map(e => e.chapterId)).size;
    const lastQ = [...submitted].reverse().find(e => e.openQuestion?.trim());
    return (
      <div style={{ ...cardBase, border: `0.5px solid rgba(201,168,76,0.12)`, marginBottom: '16px' }}>
        <div style={{ fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase', color: V.muted, fontFamily: "'Jost', sans-serif", marginBottom: '12px' }}>Your Learning Journey</div>
        <div style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", marginBottom: '8px' }}>
          You have completed feedback for <span style={{ color: V.text, fontWeight: 500 }}>{chaptersDone}</span> of 10 chapters
        </div>
        <div style={{ height: '4px', background: 'rgba(240,237,230,0.07)', borderRadius: '2px', overflow: 'hidden', marginBottom: '14px' }}>
          <div style={{ height: '100%', width: `${Math.min((chaptersDone / 10) * 100, 100)}%`, background: V.gold, borderRadius: '2px', transition: 'width 0.4s' }} />
        </div>
        {lastQ && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '10px', color: V.dim, fontFamily: "'Jost',sans-serif", marginBottom: '4px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Your last question</div>
            <p style={{ fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', lineHeight: 1.6, margin: 0 }}>"{lastQ.openQuestion}"</p>
            <div style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost',sans-serif", marginTop: '6px', lineHeight: 1.5 }}>Check the community — someone may have answered this.</div>
            <button style={{ background: 'none', border: 'none', color: V.gold, fontSize: '11px', cursor: 'pointer', fontFamily: "'Jost',sans-serif", padding: '4px 0', marginTop: '2px' }} onClick={() => onNavigate('community')}>Community →</button>
          </div>
        )}
      </div>
    );
  })();

  const mbGaugeRow = (label, value, max, zones, interpFn) => {
    if (!value && value !== 0) return null;
    const pct = Math.min(100, Math.max(0, (Number(value) / max) * 100));
    const zone = zones.find(z => Number(value) >= z.from && Number(value) < z.to) || zones[zones.length - 1];
    return (
      <div key={label} style={{ marginBottom: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '5px' }}>
          <span style={{ fontSize: '11px', color: V.muted, fontFamily: "'Jost',sans-serif" }}>{label}</span>
          <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '15px', fontWeight: 600, color: zone.color }}>{Number(value).toFixed(Number(value) < 10 ? 2 : 1)}</span>
        </div>
        <div style={{ position: 'relative', height: '8px', borderRadius: '4px', overflow: 'hidden', marginBottom: '4px' }}>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', borderRadius: '4px', overflow: 'hidden' }}>
            {zones.map((z, i) => <div key={i} style={{ flex: z.to - z.from, background: z.color, opacity: 0.55 }} />)}
          </div>
          <div style={{ position: 'absolute', top: 0, bottom: 0, width: '2px', left: `calc(${pct}% - 1px)`, background: V.text, borderRadius: '1px' }} />
        </div>
        <div style={{ fontSize: '10px', color: zone.color, fontFamily: "'Jost',sans-serif" }}>{interpFn(Number(value))}</div>
      </div>
    );
  };

  const MB_VIX_ZONES = [{from:0,to:15,color:V.sage},{from:15,to:20,color:V.gold},{from:20,to:30,color:'#c4934c'},{from:30,to:40,color:V.rose}];
  const MB_FG_ZONES  = [{from:0,to:25,color:V.rose},{from:25,to:45,color:V.gold},{from:45,to:55,color:V.dim},{from:55,to:75,color:V.sage},{from:75,to:100,color:V.rose}];

  const marketCard = (
    <div style={{ ...cardBase, marginBottom: '16px' }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <div style={{ fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: V.muted, fontFamily: "'Jost',sans-serif" }}>Market Today</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {mbLastFetch && !mbLoading && (
            <span style={{ fontSize: '10px', color: V.dim, fontFamily: "'Jost',sans-serif" }}>
              {mbMinutesAgo === 0 ? 'just now' : `${mbMinutesAgo}m ago`}
            </span>
          )}
          <button
            onClick={() => fetchMB(true)}
            disabled={mbLoading}
            style={{ background: 'transparent', border: 'none', cursor: mbLoading ? 'default' : 'pointer', color: mbLoading ? V.dim : V.muted, fontSize: '13px', padding: '2px 4px', lineHeight: 1, fontFamily: "'Jost',sans-serif" }}
            title="Refresh market data"
          >↻</button>
        </div>
      </div>

      {/* Loading state — no data yet */}
      {mbLoading && !mbData && (
        <div style={{ padding: '20px 0', textAlign: 'center' }}>
          <div style={{ fontSize: '11px', color: V.muted, fontStyle: 'italic', fontFamily: "'Jost',sans-serif" }}>Updating market data…</div>
        </div>
      )}

      {/* Live data */}
      {mbData && (
        <>
          {mbGaugeRow('VIX', mbData.vix?.value, 40, MB_VIX_ZONES, v => v < 15 ? 'Calm — favorable for sellers' : v < 20 ? 'Elevated — trade with awareness' : v < 30 ? 'Volatile — size down' : 'Extreme — consider staying flat')}
          {mbGaugeRow('Fear & Greed', mbData.fearGreed?.value, 100, MB_FG_ZONES, v => v < 25 ? 'Extreme Fear' : v < 45 ? 'Fear — options may be cheap' : v < 55 ? 'Neutral' : v < 75 ? 'Greed — be patient' : 'Extreme Greed — watch for reversal')}
          {mbData.crude && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '3px' }}>
                <span style={{ fontSize: '11px', color: V.muted, fontFamily: "'Jost',sans-serif" }}>Crude Oil</span>
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '14px', fontWeight: 600, color: V.text }}>
                  <span style={{ color: mbData.crude.direction === 'up' ? V.sage : V.rose }}>{mbData.crude.direction === 'up' ? '▲' : '▼'}</span> ${Number(mbData.crude.price).toFixed(2)}
                </span>
              </div>
              <div style={{ fontSize: '10px', color: V.dim, fontFamily: "'Jost',sans-serif" }}>{mbData.crude.context || 'Energy prices affect broad market sentiment.'}</div>
            </div>
          )}
          {mbData.gold && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '3px' }}>
                <span style={{ fontSize: '11px', color: V.muted, fontFamily: "'Jost',sans-serif" }}>Gold</span>
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '14px', fontWeight: 600, color: V.text }}>
                  <span style={{ color: mbData.gold.direction === 'up' ? V.sage : V.rose }}>{mbData.gold.direction === 'up' ? '▲' : '▼'}</span> ${Number(mbData.gold.price).toLocaleString()}
                </span>
              </div>
              <div style={{ fontSize: '10px', color: V.dim, fontFamily: "'Jost',sans-serif" }}>{mbData.gold.context || 'Gold rising signals defensive positioning.'}</div>
            </div>
          )}
          {mbData.bonds && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '3px' }}>
                <span style={{ fontSize: '11px', color: V.muted, fontFamily: "'Jost',sans-serif" }}>10Y Yield</span>
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '14px', fontWeight: 600, color: mbData.bonds.direction === 'rising' ? V.rose : V.sage }}>
                  {mbData.bonds.direction === 'rising' ? '▲' : '▼'} {Number(mbData.bonds.tenYear).toFixed(2)}%
                </span>
              </div>
              <div style={{ fontSize: '10px', color: V.dim, fontFamily: "'Jost',sans-serif" }}>{mbData.bonds.context || 'Rising yields pressure equities.'}</div>
            </div>
          )}
        </>
      )}

      {/* Fallback: no data and not loading */}
      {!mbData && !mbLoading && (
        <div style={{ padding: '10px 0 14px' }}>
          <p style={{ fontSize: '11px', color: V.dim, fontStyle: 'italic', fontFamily: "'Jost',sans-serif", margin: '0 0 10px' }}>Could not load market data. Try refreshing.</p>
        </div>
      )}

      <button style={{ ...btnGold, width: '100%', padding: '9px', fontSize: '12px', textAlign: 'center', minHeight: '44px' }} onClick={() => onNavigate('marketbrief')}>Full Brief →</button>
    </div>
  );

  const weekCard = (
    <div style={{ ...cardBase, marginBottom: '16px' }}>
      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: `0.5px solid ${V.border}`, marginBottom: '14px' }}>
        {[['events','Events'],['earnings','Earnings']].map(([t, label]) => (
          <button key={t} onClick={() => setWeekTab(t)} style={{ flex: 1, padding: '8px 0', background: 'transparent', border: 'none', borderBottom: weekTab === t ? `2px solid ${V.gold}` : '2px solid transparent', color: weekTab === t ? V.gold : V.muted, fontSize: '12px', cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontWeight: weekTab === t ? 500 : 400 }}>
            {label}
          </button>
        ))}
      </div>

      {weekTab === 'events' ? (
        <>
          <div style={{ fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: V.muted, fontFamily: "'Jost', sans-serif", marginBottom: '12px' }}>This Week</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '11px', marginBottom: '14px' }}>
            {[
              { day: 'Tue', event: 'CPI Data',      level: 'high' },
              { day: 'Thu', event: 'Jobless Claims', level: 'med'  },
              { day: 'Fri', event: 'OPEX',           level: 'high' },
            ].map(({ day, event, level }) => (
              <div key={event} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ ...mono, fontSize: '11px', color: V.dim, width: '28px', flexShrink: 0 }}>{day}</span>
                <span style={{ fontSize: '12px', color: V.text, fontFamily: "'Jost', sans-serif", flex: 1 }}>{event}</span>
                <span style={{ fontSize: '13px' }}>{level === 'high' ? '🔴' : '🟡'}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: '10px', color: V.dim, fontFamily: "'Jost',sans-serif", marginBottom: '10px' }}>🔴 High impact &nbsp; 🟡 Medium &nbsp; 🟢 Low</div>
          <p style={{ fontSize: '11px', color: V.dim, fontStyle: 'italic', fontFamily: "'Jost', sans-serif", marginBottom: '12px' }}>Full calendar coming soon</p>
          <button style={{ ...btnGold, width: '100%', padding: '9px', fontSize: '12px', textAlign: 'center', minHeight: '44px' }} onClick={() => onNavigate('calendar')}>Full Calendar →</button>
        </>
      ) : (
        <>
          <div style={{ fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: V.muted, fontFamily: "'Jost', sans-serif", marginBottom: '12px' }}>This Week's Earnings</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '14px' }}>
            {[
              { day: 'Mon', ticker: 'AAPL', name: 'Apple Inc',        timing: 'After Close' },
              { day: 'Wed', ticker: 'NVDA', name: 'NVIDIA Corp',      timing: 'Before Open' },
              { day: 'Thu', ticker: 'TSLA', name: 'Tesla Inc',        timing: 'After Close' },
              { day: 'Thu', ticker: 'AMZN', name: 'Amazon',           timing: 'After Close' },
              { day: 'Fri', ticker: 'META', name: 'Meta Platforms',   timing: 'Before Open' },
            ].map(({ day, ticker, name, timing }) => (
              <div key={ticker} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ ...mono, fontSize: '11px', color: V.dim, width: '28px', flexShrink: 0 }}>{day}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: V.gold, fontFamily: "'Jost',sans-serif", lineHeight: 1.2 }}>{ticker}</div>
                  <div style={{ fontSize: '11px', color: V.muted, fontFamily: "'Jost',sans-serif" }}>{name}</div>
                </div>
                <span style={{ fontSize: '10px', color: V.dim, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', textAlign: 'right', flexShrink: 0 }}>{timing}</span>
              </div>
            ))}
          </div>
          <div style={{ background: 'rgba(196,122,122,0.08)', border: `0.5px solid rgba(196,122,122,0.25)`, borderRadius: '8px', padding: '10px 12px', marginBottom: '12px' }}>
            <p style={{ fontSize: '12px', color: V.rose, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', lineHeight: 1.6, margin: 0 }}>
              Never sell premium into earnings without understanding the risk. When in doubt — sit out.
            </p>
          </div>
          <p style={{ fontSize: '11px', color: V.dim, fontStyle: 'italic', fontFamily: "'Jost', sans-serif", marginBottom: '12px' }}>Live earnings data coming soon</p>
          <button style={{ ...btnGold, width: '100%', padding: '9px', fontSize: '12px', textAlign: 'center', minHeight: '44px' }} onClick={() => onNavigate('calendar')}>Full Calendar →</button>
        </>
      )}
    </div>
  );

  // ── Layout ────────────────────────────────────────────────────────

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '28px 40px', maxWidth: '1200px', width: '100%', boxSizing: 'border-box' }}>
      {isMobile ? (
        <>
          {greetingCard}
          {quoteCard}
          {ruleCard}
          {riskSummaryCard}
          {whyPromptCard}
          {whyCard}
          {goalsPromptCard}
          {goalsCard}
          {communityCard}
          {learningJourneyCard}
          {tradesCard}
          {marketCard}
          {weekCard}
        </>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '65fr 35fr', gap: '0 24px', alignItems: 'start' }}>
          {/* LEFT COLUMN */}
          <div>
            {greetingCard}
            {quoteCard}
            {ruleCard}
            {riskSummaryCard}
            {whyPromptCard}
            {whyCard}
            {goalsPromptCard}
            {goalsCard}
            {communityCard}
          </div>
          {/* RIGHT COLUMN */}
          <div style={{ position: 'relative', zIndex: 1, pointerEvents: 'auto', isolation: 'isolate' }}>
            {learningJourneyCard}
            {tradesCard}
            {marketCard}
            {weekCard}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProgressDashboard
